import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const TermsOfService = () => {
    return (
        <div style={styles.page}>
            <Helmet>
                <title>Terms of Service | APILoadTest.com</title>
                <meta name="description" content="Terms of Service for APILoadTest.com. Read our terms and conditions for using the API load testing platform." />
                <link rel="canonical" href="https://www.apiloadtest.com/terms" />
                <meta name="robots" content="index, follow" />
            </Helmet>

            {/* Header */}
            <header style={styles.header}>
                <div style={styles.headerContainer}>
                    <Link to="/" style={styles.logoSection}>
                        <img src="/logo.png" alt="APILoadTest.com" style={styles.logoImage} />
                        <span style={styles.logoText}>APILoadTest.com</span>
                    </Link>
                </div>
            </header>

            <main style={styles.content}>
                <h1 style={styles.title}>Terms of Service</h1>
                <p style={styles.effectiveDate}>Effective Date: February 25, 2026</p>

                <section style={styles.section}>
                    <h2 style={styles.heading}>1. Acceptance of Terms</h2>
                    <p style={styles.paragraph}>
                        By accessing or using APILoadTest.com ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, you may not use the Service.
                    </p>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>2. Description of Service</h2>
                    <p style={styles.paragraph}>
                        APILoadTest.com provides a cloud-based API load testing platform that allows users to test the performance of their APIs under various traffic conditions. The Service includes features such as cURL import, Postman collection import, and multiple load profiles.
                    </p>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>3. User Responsibilities</h2>
                    <p style={styles.paragraph}>By using the Service, you agree that:</p>
                    <ul style={styles.list}>
                        <li>You will only test APIs that you own or have explicit authorization to test.</li>
                        <li>You will not use the Service to perform denial-of-service attacks or any form of malicious testing against third-party systems without authorization.</li>
                        <li>You are solely responsible for the API endpoints you submit for testing and any consequences arising from such testing.</li>
                        <li>You will not use the Service for any unlawful purpose or in violation of any applicable laws or regulations.</li>
                        <li>You will not attempt to reverse-engineer, decompile, or disassemble any part of the Service.</li>
                        <li>You will provide accurate information when creating an account.</li>
                    </ul>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>4. Account and Credits</h2>
                    <p style={styles.paragraph}>
                        Running test runs requires purchasing credit packs. Credits are non-expiring and remain available as long as your account is active.
                    </p>
                    <p style={styles.paragraph}>
                        You are responsible for maintaining the confidentiality of your account credentials. We are not liable for any unauthorized access to your account.
                    </p>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>5. Payment Terms</h2>
                    <p style={styles.paragraph}>
                        All purchases are processed through our third-party payment processor (Dodo Payments). Prices are listed in USD and are subject to change with notice. By making a purchase, you agree to the payment processor's terms and conditions.
                    </p>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>6. Limitation of Liability</h2>
                    <p style={styles.paragraph}>
                        THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMITTED BY LAW:
                    </p>
                    <ul style={styles.list}>
                        <li>We are not liable for any damages resulting from your use of the Service, including but not limited to damage to your APIs, servers, infrastructure, or any third-party systems.</li>
                        <li>We are not responsible for the accuracy, reliability, or availability of test results.</li>
                        <li>We are not liable for any indirect, incidental, special, consequential, or punitive damages.</li>
                        <li>Our total liability shall not exceed the amount you paid for the Service in the 12 months preceding the claim.</li>
                        <li>We are not responsible for any downtime or service interruptions.</li>
                    </ul>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>7. Indemnification</h2>
                    <p style={styles.paragraph}>
                        You agree to indemnify, defend, and hold harmless APILoadTest.com, its operators, affiliates, and their respective officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses arising from your use of the Service, your violation of these Terms, or your violation of any rights of another party.
                    </p>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>8. Intellectual Property</h2>
                    <p style={styles.paragraph}>
                        All content, features, and functionality of the Service (including but not limited to text, graphics, logos, and software) are owned by APILoadTest.com and are protected by intellectual property laws. You may not copy, modify, distribute, or create derivative works without our prior written consent.
                    </p>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>9. Termination</h2>
                    <p style={styles.paragraph}>
                        We reserve the right to suspend or terminate your account at any time, with or without cause and with or without notice. Upon termination, your right to use the Service will immediately cease. Any unused credits are non-refundable upon termination for cause.
                    </p>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>10. Modifications to Terms</h2>
                    <p style={styles.paragraph}>
                        We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting. Your continued use of the Service after changes are posted constitutes acceptance of the modified Terms.
                    </p>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>11. Governing Law</h2>
                    <p style={styles.paragraph}>
                        These Terms shall be governed by and construed in accordance with applicable laws. Any disputes arising from these Terms or your use of the Service shall be resolved through binding arbitration.
                    </p>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>12. Contact</h2>
                    <p style={styles.paragraph}>
                        For any questions about these Terms, please contact us at: <a href="mailto:admin@apiloadtest.com" style={styles.link}>admin@apiloadtest.com</a>
                    </p>
                </section>
            </main>

            {/* Footer */}
            <footer style={styles.footer}>
                <div style={styles.footerContent}>
                    <div style={styles.footerLinks}>
                        <Link to="/terms" style={styles.footerLink}>Terms of Service</Link>
                        <Link to="/privacy" style={styles.footerLink}>Privacy Policy</Link>
                        <Link to="/refund" style={styles.footerLink}>Refund Policy</Link>
                        <a href="mailto:admin@apiloadtest.com" style={styles.footerLink}>Contact</a>
                    </div>
                    <p style={styles.copyright}>© 2026 APILoadTest.com. All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
};

const styles = {
    page: {
        minHeight: '100vh',
        background: 'var(--light-gray)',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        background: 'var(--primary-white)',
        padding: '16px 0',
        borderBottom: '1px solid var(--border-color)',
    },
    headerContainer: {
        maxWidth: '800px',
        margin: '0 auto',
        padding: '0 24px',
    },
    logoSection: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        textDecoration: 'none',
    },
    logoImage: { height: '40px', width: 'auto' },
    logoText: {
        fontFamily: "'Agrandir Narrow', 'Agrandir', Arial, sans-serif",
        fontSize: '24px',
        fontWeight: '700',
        color: 'var(--text-primary)',
    },
    content: {
        maxWidth: '800px',
        margin: '0 auto',
        padding: '48px 24px 80px',
        flex: 1,
    },
    title: {
        fontFamily: "'Agrandir Narrow', 'Agrandir', Arial, sans-serif",
        fontSize: '42px',
        fontWeight: '700',
        color: 'var(--text-primary)',
        marginBottom: '8px',
    },
    effectiveDate: {
        fontSize: '14px',
        color: 'var(--text-secondary)',
        marginBottom: '48px',
    },
    section: { marginBottom: '36px' },
    heading: {
        fontSize: '22px',
        fontWeight: '700',
        color: 'var(--text-primary)',
        marginBottom: '12px',
    },
    paragraph: {
        fontSize: '16px',
        lineHeight: '1.8',
        color: 'var(--text-primary)',
        marginBottom: '12px',
    },
    list: {
        paddingLeft: '24px',
        fontSize: '16px',
        lineHeight: '1.8',
        color: 'var(--text-primary)',
        marginBottom: '12px',
    },
    link: { color: '#6366f1', textDecoration: 'none', fontWeight: '600' },
    footer: {
        background: 'var(--primary-white)',
        padding: '32px 24px',
        borderTop: '1px solid var(--border-color)',
    },
    footerContent: { maxWidth: '800px', margin: '0 auto', textAlign: 'center' },
    footerLinks: {
        display: 'flex',
        gap: '24px',
        justifyContent: 'center',
        marginBottom: '16px',
        flexWrap: 'wrap',
    },
    footerLink: {
        color: 'var(--text-secondary)',
        textDecoration: 'none',
        fontSize: '14px',
    },
    copyright: { fontSize: '14px', color: 'var(--text-secondary)' },
};

export default TermsOfService;
